import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Check, Clock, ArrowLeft, Loader2, Users, Send, Gift, Mail
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription
} from "../components/ui/card";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const plans = [
  {
    id: "monthly",
    name: "Monthly",
    price: "$7",
    interval: "month",
    description: "Perfect for getting started",
    features: [
      "Unlimited protocol sessions",
      "Full journaling access",
      "Progress tracking & badges",
      "Leaderboard access",
      "Push notifications",
    ],
    popular: false,
  },
  {
    id: "annual",
    name: "Annual",
    price: "$49",
    interval: "year",
    description: "Best value - save 42%",
    features: [
      "Everything in Monthly",
      "Save $35 per year",
      "Priority support",
      "Early access to new features",
    ],
    popular: true,
  },
];

export const Upgrade = ({ user }) => {
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [referral, setReferral] = useState(null);
  const [emails, setEmails] = useState(["", "", ""]);
  const [sending, setSending] = useState(false);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, refRes] = await Promise.all([
        axios.get(`${API}/subscription/status`, { withCredentials: true }),
        axios.get(`${API}/referral/status`, { withCredentials: true }),
      ]);
      setSubscription(subRes.data);
      setReferral(refRes.data);

      if (subRes.data.is_premium) {
        toast.success("You already have premium access!");
        navigate("/progress");
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  const handleEmailChange = (index, value) => {
    const updated = [...emails];
    updated[index] = value;
    setEmails(updated);
  };

  const handleSendInvites = async () => {
    const valid = emails.filter((e) => e.trim() && e.includes("@"));
    if (valid.length === 0) {
      toast.error("Please enter at least one valid email address");
      return;
    }
    setSending(true);
    try {
      const res = await axios.post(
        `${API}/referral/invite`,
        { emails: valid },
        { withCredentials: true }
      );
      if (res.data.sent.length > 0) {
        toast.success(`Invitations sent to ${res.data.sent.length} friend(s)!`);
        setEmails(["", "", ""]);
        fetchData();
      }
      if (res.data.failed.length > 0) {
        toast.error(`Failed to send to: ${res.data.failed.join(", ")}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitations");
    } finally {
      setSending(false);
    }
  };

  const handleExtendTrial = async () => {
    setExtending(true);
    try {
      const res = await axios.post(
        `${API}/referral/extend-trial`,
        {},
        { withCredentials: true }
      );
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to extend trial");
    } finally {
      setExtending(false);
    }
  };

  const totalInvites = referral?.total_invites || 0;
  const eligible = referral?.eligible_for_extension || false;
  const invitesNeeded = referral?.invites_needed || 3;

  return (
    <div className="min-h-screen bg-clarity-bg py-8 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate("/progress")}
          className="flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="text-center mb-10">
          <h1
            className="text-3xl sm:text-4xl text-white mb-3"
            style={{ fontFamily: "DM Serif Display, serif" }}
          >
            Continue Your Practice
          </h1>
          <p className="text-sm text-slate-400 max-w-lg mx-auto">
            Your free trial has a limit. Invite friends to extend it, or
            subscribe when plans become available.
          </p>
        </div>

        {/* Trial Status Banner */}
        {subscription && !subscription.is_premium && (
          <div className="rounded-xl p-4 mb-8 border border-amber-500/30 bg-amber-500/5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/20 flex items-center justify-center flex-shrink-0">
                <Clock className="w-5 h-5 text-amber-400" />
              </div>
              <div>
                <p className="font-medium text-white text-sm">
                  {subscription.account_type === "expired"
                    ? "Your free trial has ended"
                    : `${subscription.free_sessions_remaining} free sessions remaining`}
                </p>
                <p className="text-xs text-slate-400">
                  {subscription.trial_days_remaining > 0
                    ? `${subscription.trial_days_remaining} days left in trial`
                    : "Extend your trial by inviting friends below"}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ── REFERRAL SECTION ─────────────────────────────────── */}
        <div
          className="rounded-xl border border-[#C8922A]/30 bg-[#C8922A]/5 p-6 mb-10"
          data-testid="referral-section"
        >
          <div className="flex items-center gap-3 mb-5">
            <div className="w-10 h-10 rounded-lg bg-[#C8922A]/20 flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 text-[#C8922A]" />
            </div>
            <div>
              <h2 className="text-white text-base font-medium">
                Extend your free trial by 1 week
              </h2>
              <p className="text-xs text-slate-400">
                Invite 3 friends or family to ClarityOS
              </p>
            </div>
          </div>

          {/* Progress */}
          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full bg-[#C8922A] transition-all duration-500"
                style={{
                  width: `${Math.min(100, (totalInvites / Math.max(1, totalInvites + invitesNeeded)) * 100)}%`,
                }}
              />
            </div>
            <span className="text-xs text-slate-400 font-mono whitespace-nowrap">
              {totalInvites} / {totalInvites + invitesNeeded} sent
            </span>
          </div>

          {/* Email Inputs */}
          <div className="space-y-3 mb-5">
            {emails.map((email, i) => (
              <div key={i} className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500 flex-shrink-0" />
                <input
                  type="email"
                  placeholder={`Friend's email ${i + 1}`}
                  value={email}
                  onChange={(e) => handleEmailChange(i, e.target.value)}
                  data-testid={`referral-email-${i}`}
                  className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#C8922A]/50"
                />
              </div>
            ))}
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            <Button
              onClick={handleSendInvites}
              disabled={sending}
              data-testid="send-invites-btn"
              className="bg-[#C8922A] text-[#0c0b0a] hover:bg-[#e0a840] font-medium px-5"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : (
                <Send className="w-4 h-4 mr-2" />
              )}
              {sending ? "Sending..." : "Send Invitations"}
            </Button>

            {eligible && (
              <Button
                onClick={handleExtendTrial}
                disabled={extending}
                data-testid="extend-trial-btn"
                className="bg-emerald-600 text-white hover:bg-emerald-500 font-medium px-5"
              >
                {extending ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Gift className="w-4 h-4 mr-2" />
                )}
                {extending ? "Extending..." : "Extend Trial +1 Week"}
              </Button>
            )}
          </div>

          {/* Sent invites list */}
          {referral?.invites?.length > 0 && (
            <div className="mt-5 pt-4 border-t border-white/5">
              <p className="text-xs text-slate-500 mb-2 font-mono uppercase tracking-wider">
                Invitations Sent
              </p>
              <div className="space-y-1.5">
                {referral.invites.map((inv, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 text-xs text-slate-400"
                  >
                    <Users className="w-3 h-3 text-[#C8922A]" />
                    <span>{inv.invitee_email}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* ── PLANS (Coming Soon) ───────────────────────────── */}
        <div className="mb-10">
          <div className="text-center mb-6">
            <h2
              className="text-xl text-white mb-2"
              style={{ fontFamily: "DM Serif Display, serif" }}
            >
              Subscription Plans
            </h2>
            <p className="text-xs text-slate-500 font-mono uppercase tracking-wider">
              Coming Soon
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 opacity-60">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative overflow-hidden ${
                  plan.popular
                    ? "border-[#C8922A]/30 bg-[#C8922A]/5"
                    : "border-white/10 bg-white/[0.02]"
                }`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 bg-[#C8922A] text-[#0c0b0a] text-xs font-bold px-3 py-1 rounded-bl-lg">
                    BEST VALUE
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg text-white">
                    {plan.name}
                  </CardTitle>
                  <CardDescription className="text-slate-400 text-xs">
                    {plan.description}
                  </CardDescription>
                  <div className="mt-3">
                    <span className="text-3xl font-bold text-white">
                      {plan.price}
                    </span>
                    <span className="text-slate-400 text-sm">
                      /{plan.interval}
                    </span>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2 mb-5">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-[#3baa7a] flex-shrink-0" />
                        <span className="text-slate-300 text-xs">
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    disabled
                    className="w-full bg-white/10 text-slate-400 cursor-not-allowed font-medium py-2.5 text-sm"
                    data-testid={`upgrade-btn-${plan.id}`}
                  >
                    Coming Soon
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Book Verification Link */}
        <div className="text-center mb-6">
          <button
            onClick={() => navigate("/book-verification")}
            className="text-[#C8922A] hover:underline text-xs"
            data-testid="book-verification-link"
          >
            I have the book — get 6 months free &rarr;
          </button>
        </div>
      </div>
    </div>
  );
};

export default Upgrade;
